import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { X } from 'lucide-react';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { useAbility } from '@/providers/';
import { useAuthContext } from '@/auth';
import { Container } from '@/components/container';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { createDataTableOptions, createDataTableConfig, getAccessToken } from '@/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import sharedStorageService from '@/services/shared-storage/sharedStorageService';

DataTable.use(DT);

const createSchema = Yup.object({
  name: Yup.string().trim().required('Please give this space a name.').max(255),
  description: Yup.string().nullable().max(2000),
  expires_at: Yup.string().nullable(),
});

/**
 * Shared Storage — the list of spaces the firm has created for sharing
 * documents outside the firm.
 */
const SharedStorageList = () => {
  const { logout } = useAuthContext();
  const { ability } = useAbility();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef({});

  const refresh = () => tableRef.current?.dt()?.ajax.reload();

  const columns = [
    { title: 'Name', name: 'name', data: 'name' },
    {
      title: 'Status',
      name: 'status',
      data: 'status',
      render: (value, type, row) => {
        const expired = row.expires_at && new Date(row.expires_at) < new Date();
        const label = value === 'revoked' ? 'revoked' : expired ? 'expired' : 'active';
        const badge =
          label === 'active' ? 'badge-success' : label === 'expired' ? 'badge-warning' : 'badge-danger';
        return `<span class="badge badge-pill badge-outline capitalize ${badge}">${label}</span>`;
      },
    },
    {
      title: 'Recipients',
      name: 'recipients',
      data: null,
      orderable: false,
      searchable: false,
      render: (data, type, row) => (row.recipients?.length ?? 0),
    },
    {
      title: 'Documents',
      name: 'documents',
      data: 'documents_count',
      orderable: false,
      searchable: false,
      defaultContent: '0',
      render: (value) => `<span class="badge badge-pill badge-outline">${value ?? 0}</span>`,
    },
    { title: 'Expires', name: 'expires_at', data: 'expires_at', defaultContent: 'No expiry' },
    { title: 'Created', name: 'created_at', data: 'created_at' },
    {
      title: 'Actions',
      name: 'actions',
      data: null,
      orderable: false,
      searchable: false,
      render: (data, type, row) => `<div id="space-action-${row.id}"></div>`,
    },
  ];

  const defaultVisibleColumns = ['name', 'status', 'recipients', 'documents', 'expires_at', 'actions'];

  const handleCreate = async (values, { setSubmitting, setErrors }) => {
    try {
      const { status, data } = await sharedStorageService.createSpace({
        name: values.name,
        description: values.description || null,
        expires_at: values.expires_at || null,
      });
      if (status) {
        setShowCreate(false);
        toast.success('Space created.', { description: 'Add documents and recipients next.' });
        navigate(`/shared-storage/${data.id}`);
      }
    } catch (error) {
      const errors = error?.response?.data?.errors;
      if (errors) {
        setErrors(Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, v[0]])));
        return;
      }
      toast.error('Could not create the space.', { description: 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await sharedStorageService.revokeSpace(id);
      refresh();
      toast.success('Access revoked.', { description: 'Every recipient has been cut off immediately.' });
    } catch {
      toast.error('Could not revoke access.', { description: 'Please try again in a moment.' });
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      await sharedStorageService.deleteSpace(actionId);
      refresh();
      toast.success('Space deleted.', { description: 'The documents in it were permanently removed.' });
    } catch {
      toast.error('Could not delete the space.', { description: 'Please try again in a moment.' });
    }
  };

  const renderActions = (row) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="text-gray-600">
          <KeenIcon icon="dots-vertical" className="text-[18px]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => navigate(`/shared-storage/${row.id}`)}>
          <KeenIcon icon="folder" className="mr-0.5 h-4 w-4" /> Open
        </DropdownMenuItem>
        <Can I="handle" a="shared-storage" ability={ability}>
          {row.status !== 'revoked' && (
            <DropdownMenuItem onClick={() => handleRevoke(row.id)}>
              <KeenIcon icon="lock" className="mr-0.5 h-4 w-4" /> Revoke access
            </DropdownMenuItem>
          )}
        </Can>
        <Can I="delete" a="shared-storage" ability={ability}>
          <DropdownMenuItem onClick={() => openModal(row.id)} className="text-red-600">
            <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </Can>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const ajaxConfig = createDataTableConfig('/shared-spaces/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    // Created (hidden) — index shifts if a column is added before it.
    order: [[5, 'desc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) col.visible(false);
      });
    },
    drawCallback: function () {
      const api = this.api();
      api
        .rows({ page: 'current' })
        .data()
        .each((row) => {
          const container = document.getElementById(`space-action-${row.id}`);
          if (!container) return;
          if (!container._reactRoot) container._reactRoot = createRoot(container);
          container._reactRoot.render(renderActions(row));
        });
    },
  });

  return (
    <Container>
      <div className="card min-w-full mb-4">
        <div className="card-header">
          <div>
            <h3 className="card-title">Secure File Sharing</h3>
            <p className="text-2sm text-gray-700">
              Share case documents with a client or third party through a private link
              protected by a PIN, which you can expire or revoke at any time.
            </p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <Can I="handle" a="shared-storage" ability={ability}>
              <button className="text-xs btn btn-sm btn-primary" onClick={() => setShowCreate(true)}>
                <KeenIcon icon="plus" className="w-fit btn-icon" />
                Create Space
              </button>
            </Can>
          </div>
        </div>

        <DataTable
          id="shared-spaces-table"
          ajax={ajaxConfig}
          columns={columns}
          options={options}
          ref={tableRef}
        />
      </div>

      {showCreate && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-lg">
            <button type="button" onClick={() => setShowCreate(false)} className="modal-close-btn">
              <X size={16} />
            </button>
            <h2 className="text-lg font-medium text-gray-900 my-2">Create Space</h2>

            <Formik
              initialValues={{ name: '', description: '', expires_at: '' }}
              validationSchema={createSchema}
              onSubmit={handleCreate}
            >
              {({ isSubmitting }) => (
                <Form className="flex flex-col gap-3 mt-2">
                  <div>
                    <label className="form-label">Name</label>
                    <Field name="name" className="input w-full" placeholder="e.g. Records for Dr. Patel" />
                    <ErrorMessage name="name" component="p" className="text-danger text-2xs mt-1" />
                    <p className="text-2xs text-gray-500 mt-1">
                      Recipients never see this name — it is for your team only.
                    </p>
                  </div>

                  <div>
                    <label className="form-label">Description</label>
                    <Field as="textarea" name="description" rows={3} className="textarea w-full" />
                  </div>

                  <div>
                    <label className="form-label">Access ends on</label>
                    <Field type="date" name="expires_at" className="input w-full" />
                    <p className="text-2xs text-gray-500 mt-1">
                      Leave blank for no expiry. After this date nobody can open the space.
                    </p>
                  </div>

                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button type="button" className="btn btn-outline" onClick={() => setShowCreate(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating…' : 'Create'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      <ConfirmationModal
        show={isOpen}
        onClose={closeModal}
        onConfirm={handleDelete}
        title="Delete this space?"
        message="The documents inside it will be permanently deleted and every recipient loses access. The access history is kept. This cannot be undone."
      />
    </Container>
  );
};

export { SharedStorageList };
